import _ from "lodash";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import classNames from "classnames";
import { graphql, compose } from "lib/apolloClient";

import {
  Dropdown,
  DropdownMenu,
  IconButton,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  ChevronLeftOutlined,
  CloseOutlined,
  DotsHorizontalOutlined,
  ExpandArrowsFourOutlined,
  ArrowOutOutlined,
  SettingsOutlined,
} from "@toddle-edu/ds-icons";
import { useI18n } from "Hooks";
import {
  CHAT_TYPE,
  getIsMessagingWidget,
  getIsViewMode,
  isManageChannelsEnabledForNonCreator,
  CHAT_SCREEN_TYPE,
  CHAT_TYPE_ENUM,
  isCreateAndManageOwnChannelEnabled,
  recordChatActions,
  MESSAGING_ACTIONS_FROM,
  MESSAGING_ACTIONS,
  recordMessagingWidgetExpand,
  getIsSidePaneMode,
  isAddMembersV3Enabled,
  isChatToChannelEnabled,
  OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS,
  isMessagingRevampV2Enabled,
  isChannelSplitFeatureEnabled,
  getMessagingBaseRoute,
} from "MessagingV2/modules/Utils";
import { getMessageV2UserDetailFromCache } from "MessagingV2/components/Settings/modules/SettingGraphqlHelper";
import { getMessageV2UserDetailQuery } from "MessagingV2/components/Settings/modules/SettingQueries";
import { isUserTypeStaff } from "Utils";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import StudentAlerts from "AppComponents/StudentAlerts";
import {
  useChatOptions,
  withChatOptions,
} from "MessagingV2/providers/ChatOptionsProvider";
import { getIsAdminTab } from "Announcements/modules/utils";
import {
  toggleChatMute,
  setMessagingSections,
  getMessagingV2ReduxState,
  setShowOverviewModal,
} from "MessagingV2/modules/MessagingModule";
import { getUserCourseIdsQuery } from "MessagingV2/modules/MessagingQuery";

import withLoader from "UIComponents/GqlWithLoader";
import {
  getDefaultNameSortPreference,
  getUserType,
  getUserEntityType,
  getOrganizationId,
  getDefaultStudentNameOrderPreference,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import {
  getIsCombineCurriculumHomePageEnabled,
  goToRoute,
} from "modules/NavigationModule";

import {
  channelActionOptions,
  chatActionHandler,
  channelActionHandler,
  chatActionOptions,
  getHeaderTextForChat,
  getHeaderTextForChannel,
  getHeaderChatAvatarProps,
  getHeaderChannelAvatarProps,
  CHANNEL_SETTINGS,
  EDIT_INFO,
  CHANNEL_OVERVIEW_AND_SETTINGS,
  MANAGE_MEMBERS,
  ARCHIVE,
  UNARCHIVE,
  ADD_MEMBERS,
} from "./HeaderUtils";
import classes from "./Header.scss";
import EditChannel from "MessagingV2/components/Channels/EditChannel";
import OverviewAndSettingsModal from "MessagingV2/components/Channels/OverviewAndSettingsModal";
import AddMembersV2 from "MessagingV2/components/Channels/AddMembersV2";
import AddMembersV3 from "MessagingV2/components/Channels/AddMembersV3";

import MemberList from "MessagingV2/components/Channels/MemberList";
import ChannelArchiveModal from "MessagingV2/components/Channels/ChannelArchiveModal";
import ChannelSettingModal from "MessagingV2/components/Channels/ChannelSettingModal";
import { AvatarWithAccessPoint } from "MessagingV2/components/ThreadList/Avatar";
import ChannelUnArchiveModal from "MessagingV2/components/Channels/ChannelUnArchiveModal";
import ChatToChannel from "MessagingV2/components/ChatToChannel";

const getAvatarStyles = () => {
  return {
    size: "medium",
    shade: "light",
    randomizeColor: true,
  };
};

const Header = props => {
  const { t, defaultNameSortPreference } = useI18n(["common"]);

  const {
    isArchived,
    isMuted,
    description,
    profileImage,
    profileIcon,
    users,
    chatType,
    toggleChatMute,
    channelActionHandler,
    setSegmentChatType,
    setMessagingSections,
    goToRoute,
    onBackClickFromWidget = () => {},
    onCloseChatContainer = () => {},
    currentUserRole,
    channelLabel,
    bannerMessage,
    isUserStaff,
    currentLoggedInUserId,
    conversationType,
    personalChatReceiverUser = {},
    viewOnlyRoles,
    isArchivedChatDetail,
    screenType,
    isUserCreator,
    activeUserCourseIds,
    defaultStudentNameOrderPreference,
    selectedCurriculumId,
    isAdminTab,
    createdBy,
    showOverviewModal,
    setShowOverviewModal,
    isAccountPortal,
    isMembersListVisible,
    memberListHiddenRoles,
  } = props;

  const isChat = chatType === CHAT_TYPE.CHAT;
  const showAddMembersV3 = isAddMembersV3Enabled();
  const hideChannelMemberTab = !isMembersListVisible;
  const { chatId, screenMode } = useChatOptions();
  const isFamilyChannel = conversationType === CHAT_TYPE_ENUM.CHANNEL_FAMILY;

  const isWidget = getIsMessagingWidget({ screenMode });
  const isSidePane = getIsSidePaneMode({ screenMode });
  const searchMode = screenType === CHAT_SCREEN_TYPE.SEARCH;

  const toggleChatMuteHandler = () => {
    toggleChatMute({ id: chatId, isMuted });
    recordChatActions({
      action: !isMuted ? MESSAGING_ACTIONS.MUTE : MESSAGING_ACTIONS.UNMUTE,
      chatId,
      from: MESSAGING_ACTIONS_FROM.CHAT_ACTIONS,
      chatType: conversationType,
    });
  };

  const getDefaultTab = () => {
    switch (conversationType) {
      case CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED:
      case CHAT_TYPE_ENUM.CHANNEL_FAMILY:
        return hideChannelMemberTab
          ? OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.SETTINGS
          : OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.MEMBERS;
      case CHAT_TYPE_ENUM.CHANNEL_CUSTOM:
      case CHAT_TYPE_ENUM.CHANNEL_PUBLIC:
      case CHAT_TYPE_ENUM.CHANNEL_DEPARTMENT:
        return hideChannelMemberTab
          ? OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.ABOUT
          : OVERVIEW_AND_SETTING_CHANNEL_TABS_CONSTANTS.MEMBERS;
    }
  };

  const isPublicChannelEnable = isChannelSplitFeatureEnabled();
  const showMemberList =
    !isChat &&
    (isUserStaff || isPublicChannelEnable) &&
    !isWidget &&
    !searchMode &&
    !_.isEmpty(users) &&
    !(hideChannelMemberTab && isFamilyChannel);

  const defaultOverviewSettingTab = getDefaultTab();

  const isCurrentUserNotInChannel = !_.find(users, {
    id: currentLoggedInUserId,
  });

  const channelDescription =
    conversationType === CHAT_TYPE_ENUM.CHANNEL_SYSTEM_MANAGED
      ? t("common:system_managed_channel_description_with_label", {
          label: channelLabel,
        })
      : description;

  const canManageChannel =
    (isUserCreator && isCreateAndManageOwnChannelEnabled()) ||
    (!isUserCreator && isManageChannelsEnabledForNonCreator());

  const isMemberUpdate =
    _.includes(
      [CHAT_TYPE_ENUM.CHANNEL_CUSTOM, CHAT_TYPE_ENUM.CHANNEL_PUBLIC],
      conversationType
    ) &&
    canManageChannel &&
    !isCurrentUserNotInChannel;

  const actionOptions = isChat
    ? chatActionOptions({
        t,
        isArchived,
        isMuted,
        onClick: toggleChatMuteHandler,
      })
    : channelActionOptions({
        t,
        isArchived: isArchivedChatDetail,
        isMuted,
        isCurrentUserNotInChannel,
        conversationType,
        canManageChannel,
        toggleChatMuteHandler,
      });

  const [optionsModalState, setOptionsModalState] = useState(null);

  const handleCloseDropDownModals = () => {
    setOptionsModalState(null);
  };

  const handleCloseOverviewModal = () => {
    setOptionsModalState(null);
    setShowOverviewModal(false);
  };

  const isUIRevampEnabledV2 = isMessagingRevampV2Enabled();

  const actionHandler = ({ key }) =>
    isChat
      ? chatActionHandler({
          key,
          chatId,
          conversationType,
          isMuted,
          toggleChatMute,
          setOptionsModalState,
        })
      : channelActionHandler({
          key,
          chatId,
          isMuted,
          conversationType,
          isMemberUpdate,
          setChannelOptionsModalState: setOptionsModalState,
          t,
        });

  const { headerTitle, subHeader } = isChat
    ? getHeaderTextForChat({
        loggedInUserRole: currentUserRole,
        user: personalChatReceiverUser,
        t,
        activeUserCourseIds,
        defaultNameSortPreference,
        defaultStudentNameOrderPreference,
        isWidget,
        isUIRevampEnabledV2,
      })
    : getHeaderTextForChannel({
        label: channelLabel,
        description: channelDescription,
        isWidget,
      });

  const navigateToMessagingPage = () => {
    setMessagingSections({
      section: isChat ? CHAT_TYPE.CHAT : CHAT_TYPE.CHANNEL,
    });

    const isCombinedCurriculumHomePage = getIsCombineCurriculumHomePageEnabled();
    const baseRoute = getMessagingBaseRoute({
      isCombinedCurriculumHomePage,
      isAdminTab,
      isAccountPortal,
      curriculumId: selectedCurriculumId,
    });

    goToRoute({
      route: `${baseRoute}/messaging/${chatId}`,
      isOpenInNewTab: isUIRevampEnabledV2,
    });

    recordMessagingWidgetExpand({
      chatId,
      chatType: conversationType,
    });
  };

  const showDropdown =
    !_.isEmpty(actionOptions) &&
    !searchMode &&
    !(isPublicChannelEnable && !isChat) &&
    (!isChat || isChatToChannelEnabled());

  const showChatActionOptions =
    React.isValidElement(actionOptions) &&
    (isChat || isPublicChannelEnable) &&
    !searchMode;

  const closeChatOption = searchMode;

  const avatarProps = isChat
    ? getHeaderChatAvatarProps({
        user: personalChatReceiverUser,
        defaultNameSortPreference,
        isArchived,
      })
    : getHeaderChannelAvatarProps({
        channelLabel,
        chatType: conversationType,
        profileImage,
        profileIcon,
        chatId,
      });

  const headerContainerV2 = classNames(classes.headerContainerV2, {
    [classes.headerContainerWidgetV2]: isWidget,
    [classes.headerSidePaneContainer]: isSidePane,
    [classes.headerContainerWidgetV3]: isWidget && isMessagingRevampV2Enabled(),
    [classes.headerContainerV3]: isMessagingRevampV2Enabled(),
  });

  const headerTextClassesV2 = classNames(classes.headerTitle, "text-label-l");

  const subHeaderTextClasses = classNames("text-body-s", classes.textSubtle, {
    [classes.textSubtleV2]: isUIRevampEnabledV2,
  });

  const handleMemberListClick = () => {
    setOptionsModalState(CHANNEL_OVERVIEW_AND_SETTINGS);
  };

  const showRevampedStyle = !isUIRevampEnabledV2;

  return (
    <div className={headerContainerV2} dir="auto">
      {isWidget ? (
        <IconButton
          icon={<ChevronLeftOutlined />}
          variant="plain"
          onClick={onBackClickFromWidget}
          className={classNames({
            [classes.iconButton]: !isUIRevampEnabledV2,
          })}
          size={showRevampedStyle ? "small" : "medium"}
          {...(isUIRevampEnabledV2 && { dsVersion: "2.0", type: "plain" })}
        />
      ) : null}

      <div className={classes.avatarDetailContainerV2}>
        <Tooltip tooltip={channelLabel} placement="bottom">
          <div>
            <AvatarWithAccessPoint
              {...avatarProps}
              hideChatWithUser
              customAvatarStyles={getAvatarStyles()}
              customAvatarContainerClassName={classes.avatarContainer}
            />
          </div>
        </Tooltip>
        <div className={classes.detailContainerWrapper}>
          <div className={classes.detailContainer}>
            <div className={classes.headerTitleContainer}>
              <div className={headerTextClassesV2}>{headerTitle}</div>
              {personalChatReceiverUser &&
              !_.isEmpty(personalChatReceiverUser.alertTypes) ? (
                <StudentAlerts
                  studentId={personalChatReceiverUser.id}
                  alertTypes={personalChatReceiverUser.alertTypes}
                />
              ) : null}
            </div>
            {!isUIRevampEnabledV2 ? null : (
              <div className={subHeaderTextClasses}>{subHeader}</div>
            )}
          </div>
        </div>
      </div>

      {isWidget ? (
        <Tooltip tooltip={t("common:full_screen_mode")} placement="top">
          <IconButton
            icon={
              isUIRevampEnabledV2 ? (
                <ArrowOutOutlined />
              ) : (
                <ExpandArrowsFourOutlined />
              )
            }
            variant={showRevampedStyle ? "outlined-subtle" : "plain"}
            size={showRevampedStyle ? "small" : "medium"}
            className={classNames({
              [classes.iconButton]: !isUIRevampEnabledV2,
            })}
            {...(isUIRevampEnabledV2 && { dsVersion: "2.0", type: "plain" })}
            onClick={navigateToMessagingPage}
          />
        </Tooltip>
      ) : null}

      {closeChatOption ? (
        <IconButton
          icon={<CloseOutlined />}
          variant="plain"
          size={"small"}
          className={classNames({
            [classes.iconButton]: true,
          })}
          onClick={onCloseChatContainer}
        />
      ) : null}

      {showMemberList ? (
        hideChannelMemberTab ? (
          <IconButton
            icon={<SettingsOutlined />}
            onClick={handleMemberListClick}
            dsVersion="2.0"
            variant="neutral"
            type="outlined"
            size="medium"
          />
        ) : (
          <MemberList
            users={users}
            channelLabel={channelLabel}
            isArchived={isArchivedChatDetail}
            isMemberUpdate={isMemberUpdate}
            setChannelOptionsModalState={handleMemberListClick}
          />
        )
      ) : null}

      {showDropdown ? (
        <div className={classNames({ [classes.dropDownContainer]: !isWidget })}>
          <Dropdown
            overlay={
              <DropdownMenu
                className={classes.dropDownMenuContainer}
                dsVersion="2.0"
                size="medium"
                options={actionOptions}
                onClick={actionHandler}
              />
            }
          >
            {isUIRevampEnabledV2 ? (
              <IconButton
                size="medium"
                variant={isWidget ? "plain" : "neutral"}
                dsVersion="2.0"
                type="outlined"
                icon={<DotsHorizontalOutlined />}
              />
            ) : (
              <IconButton
                size={"small"}
                variant={"outlined-subtle"}
                className={classes.iconButton}
                icon={<DotsHorizontalOutlined />}
              />
            )}
          </Dropdown>
        </div>
      ) : null}

      {showChatActionOptions ? actionOptions : null}

      {_.isEqual(optionsModalState, CHANNEL_OVERVIEW_AND_SETTINGS) ||
      showOverviewModal ? (
        <OverviewAndSettingsModal
          onCloseModal={handleCloseOverviewModal}
          chatId={chatId}
          channelLabel={channelLabel}
          channelBannerMessage={bannerMessage}
          channelDescription={channelDescription}
          channelProfileImage={profileImage}
          viewOnlyRoles={viewOnlyRoles}
          isMuted={isMuted}
          isMembersListVisible={isMembersListVisible}
          memberListHiddenRoles={memberListHiddenRoles}
          channelType={conversationType}
          profileIcon={profileIcon}
          users={users}
          createdBy={createdBy}
          isUserPartOfChannel={!isCurrentUserNotInChannel}
          isMemberUpdate={isMemberUpdate}
          defaultTab={defaultOverviewSettingTab}
          canManageChannel={canManageChannel}
        />
      ) : null}
      {_.isEqual(optionsModalState, EDIT_INFO) ? (
        <EditChannel
          onCloseModal={handleCloseDropDownModals}
          chatId={chatId}
          channelLabel={channelLabel}
          channelDescription={channelDescription}
          channelProfileImage={profileImage}
        />
      ) : null}
      {_.isEqual(optionsModalState, CHANNEL_SETTINGS) ? (
        <ChannelSettingModal
          onCloseModal={handleCloseDropDownModals}
          chatId={chatId}
          channelLabel={channelLabel}
          viewOnlyRoles={viewOnlyRoles}
          channelType={conversationType}
        />
      ) : null}
      {_.isEqual(optionsModalState, ARCHIVE) ? (
        <ChannelArchiveModal
          chatId={chatId}
          onCloseModal={handleCloseDropDownModals}
          channelLabel={channelLabel}
        />
      ) : null}
      {_.isEqual(optionsModalState, UNARCHIVE) ? (
        <ChannelUnArchiveModal
          chatId={chatId}
          onCloseModal={handleCloseDropDownModals}
          channelLabel={channelLabel}
        />
      ) : null}
      {_.isEqual(optionsModalState, MANAGE_MEMBERS) ? (
        showAddMembersV3 ? (
          <AddMembersV3
            users={users}
            onCloseModal={handleCloseDropDownModals}
            chatId={chatId}
            channelLabel={channelLabel}
            isMemberUpdate={isMemberUpdate}
          />
        ) : (
          <AddMembersV2
            onCloseModal={handleCloseDropDownModals}
            users={users}
            chatId={chatId}
            channelLabel={channelLabel}
            isMemberUpdate={isMemberUpdate}
            isUpdateFlow
          />
        )
      ) : null}
      {_.isEqual(optionsModalState, ADD_MEMBERS) ? (
        <ChatToChannel
          onCloseModal={handleCloseDropDownModals}
          chatId={chatId}
          conversationType={conversationType}
          users={isChat ? [personalChatReceiverUser] : users}
        />
      ) : null}
    </div>
  );
};

Header.propTypes = {
  isArchived: PropTypes.bool.isRequired,
  isMuted: PropTypes.bool.isRequired,
  isMembersListVisible: PropTypes.bool.isRequired,
  memberListHiddenRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  chatType: PropTypes.string.isRequired,
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  onBackClickFromWidget: PropTypes.func,
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = getUserInfo();
  const { user_type: userType, id: currentLoggedInUserId } = userInfo;
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );

  const messagingState = getMessagingV2ReduxState(state);

  const isUserStaff = isUserTypeStaff({ userType });

  const activeUserId = _.get(ownProps, "activeUserId");
  const screenMode = _.get(ownProps, "screenMode");
  const isViewMode = getIsViewMode({ screenMode });
  const currentUserDetailQueryVariables = {
    id: activeUserId,
    type: getUserEntityType(),
  };

  const viewModeUserDetailQueryVariables = {
    organizationId: getOrganizationId(),
    input: {
      first: 1,
      userType: ["staff", "student", "parent"],
      includedUserIds: [activeUserId],
    },
  };

  const showOverviewModal = _.get(messagingState, "showOverviewModal", false);

  return {
    currentUserRole: getUserType(state),
    defaultNameSortPreference: getDefaultNameSortPreference(state),
    currentUserDetailQueryVariables,
    viewModeUserDetailQueryVariables,
    isViewMode,
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    isUserStaff,
    currentLoggedInUserId,
    isAdminTab: getIsAdminTab(state),
    isAccountPortal: _.get(state, "login.activeTab") === "accountSettingsTab",
    containerStyle: { display: "contents" },
    selectedCurriculumId: getSelectedCurriculumId(state),
    showOverviewModal,
  };
};

const mapActionCreators = {
  toggleChatMute,
  setMessagingSections,
  channelActionHandler,
  goToRoute,
  setShowOverviewModal,
};

export default compose(
  withChatOptions(),
  connect(mapStateToProps, mapActionCreators),
  graphql(getUserCourseIdsQuery, {
    name: "getUserCourseIds",
    skip: ({ isViewMode }) => isViewMode,
    options: ({ currentUserDetailQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: currentUserDetailQueryVariables,
    }),
    props: ({ getUserCourseIds }) => {
      const activeUserCourseIds = _.map(
        _.get(getUserCourseIds, "node.allCourses", []),
        "id"
      );

      return {
        activeUserCourseIds,
        isData: !_.isEmpty(getUserCourseIds?.node),
      };
    },
  }),
  graphql(getMessageV2UserDetailQuery, {
    name: "getMessageV2UserDetail",
    skip: ({ isViewMode }) => !isViewMode,
    options: props => {
      const { viewModeUserDetailQueryVariables } = props;
      return {
        fetchPolicy: "cache-only",
        variables: viewModeUserDetailQueryVariables,
      };
    },
    props: ({ ownProps: { viewModeUserDetailQueryVariables } }) => {
      const cachedData = getMessageV2UserDetailFromCache(
        viewModeUserDetailQueryVariables
      );
      const userNodeList = _.get(cachedData, "messageV2Users.edges", []);
      const userNode = _.first(userNodeList);
      const selectedUser = _.get(userNode, "node", {});

      const activeUserCourseIds = _.map(
        _.get(selectedUser, "courses", []),
        "id"
      );
      return {
        activeUserCourseIds,
        isData: !_.isEmpty(cachedData),
      };
    },
  }),
  withLoader
)(Header);
